import AWS from 'aws-sdk';
import axios from 'axios';
import environment from '../config/environment';

class ExternalAnalyticsService {
  constructor() {
    this.env = environment();
    this.initializeAWS();
    this.cloudflareData = null;
    
    // Initialize API keys from environment variables
    this.cloudflareApiKey = process.env.REACT_APP_CLOUDFLARE_API_KEY;
    this.cloudflareApiToken = process.env.REACT_APP_CLOUDFLARE_API_TOKEN;
    this.cloudflareZoneId = process.env.REACT_APP_CLOUDFLARE_ZONE_ID;
    this.cloudflareEmail = process.env.REACT_APP_CLOUDFLARE_EMAIL;
    
    // Analytics tracking configuration
    this.isEnabled = process.env.REACT_APP_ENABLE_ANALYTICS === 'true';
    this.sessionId = this.generateSessionId();
    this.userId = null;
    this.organizationId = null;
    this.heatMapData = [];
    this.debug = process.env.NODE_ENV === 'development';
    
    // Use local API endpoint for analytics to avoid CORS issues
    this.analyticsApiUrl = '/api/analytics';
    
    // Enhanced monitoring thresholds
    this.thresholds = {
      s3: {
        storageUtilization: 80, // Alert at 80% capacity
        errorRate: 5 // Alert at 5% error rate
      },
      cloudflare: {
        threatScore: 50,
        errorRate: 2,
        cacheHitRate: 60
      },
      mongodb: {
        responseTime: 100, // ms
        connectionLimit: 80, // % of max connections
        errorRate: 1
      },
      postgresql: {
        queryTime: 200, // ms
        connectionUsage: 75, // % of max connections
        diskUsage: 85 // % of allocated space
      }
    };
    
    // Initialize tracking
    if (this.isEnabled) {
      this.initializeTracking();
    }

    this.baseUrl = '/api/proxy';
    
    // Log AWS credentials being used (first few characters only for security)
    console.log('AWS credentials being used:');
    console.log('Access Key ID (first 4 chars):', process.env.REACT_APP_AWS_ACCESS_KEY_ID?.substring(0, 4));
    console.log('Secret Key exists:', !!process.env.REACT_APP_AWS_SECRET_ACCESS_KEY);
    console.log('Region:', process.env.REACT_APP_AWS_REGION);
  }

  // Generate a unique session ID
  generateSessionId() {
    return 'session_' + Math.random().toString(36).substring(2, 15) + 
           Math.random().toString(36).substring(2, 15) + 
           '_' + Date.now();
  }

  initializeTracking() {
    // Initialize tracking functionality
    console.log('Analytics tracking initialized with session ID:', this.sessionId);
    
    // You can add more tracking initialization code here
    // For example, setting up event listeners, etc.
  }

  initializeAWS() {
    // Check if AWS credentials are available
    const accessKeyId = process.env.REACT_APP_AWS_ACCESS_KEY_ID;
    const secretAccessKey = process.env.REACT_APP_AWS_SECRET_ACCESS_KEY;
    const region = process.env.REACT_APP_AWS_REGION || 'us-east-1';
    
    if (!accessKeyId || !secretAccessKey) {
      console.warn('AWS credentials not found. AWS metrics will not be available.');
      this.awsConfigured = false;
      return;
    }
    
    // Configure AWS SDK
    AWS.config.update({
      region,
      accessKeyId,
      secretAccessKey
    });
    
    // Initialize AWS services
    this.s3 = new AWS.S3();
    this.cloudwatch = new AWS.CloudWatch();
    this.cloudfront = new AWS.CloudFront();
    this.lambda = new AWS.Lambda();
    this.dynamodb = new AWS.DynamoDB();
    
    this.awsConfigured = true;
    console.log('AWS SDK initialized successfully');
  }

  // Main method to get all external analytics data
  async getAllExternalAnalytics() {
    try {
      console.log('Fetching all external analytics data...');
      
      // Fetch AWS metrics
      const awsData = await this.getAWSMetrics();
      
      // Fetch Cloudflare metrics
      const cloudflareData = await this.getCloudflareMetrics();
      
      return {
        aws: awsData,
        cloudflare: cloudflareData
      };
    } catch (error) {
      console.error('Error fetching external analytics:', error);
      return {
        error: error.message || 'Failed to fetch external analytics data'
      };
    }
  }

  // Get AWS metrics
  async getAWSMetrics() {
    try {
      console.log('Fetching AWS metrics...');
      
      // Get AWS credentials from environment variables
      const accessKeyId = process.env.REACT_APP_AWS_ACCESS_KEY_ID;
      const secretAccessKey = process.env.REACT_APP_AWS_SECRET_ACCESS_KEY;
      const region = process.env.REACT_APP_AWS_REGION || 'us-east-1';
      
      if (!accessKeyId || !secretAccessKey) {
        console.error('AWS credentials not found in environment variables');
        return {
          error: 'AWS credentials not configured. Please check your environment variables.'
        };
      }
      
      // Set headers with AWS credentials
      const headers = {
        'x-aws-access-key-id': accessKeyId,
        'x-aws-secret-access-key': secretAccessKey,
        'x-aws-region': region
      };
      
      // Fetch S3 buckets
      const s3Response = await axios.get(`${this.baseUrl}/aws/s3/buckets`, { headers });
      
      // Fetch CloudWatch metrics
      const cloudwatchResponse = await axios.get(`${this.baseUrl}/aws/cloudwatch/metrics`, { headers });
      
      // Fetch Lambda functions
      const lambdaResponse = await axios.get(`${this.baseUrl}/aws/lambda/functions`, { headers })
        .catch(error => {
          console.error('Error fetching Lambda functions:', error);
          return { data: { error: error.response?.data?.error || error.message } };
        });
      
      // Fetch RDS instances
      const rdsResponse = await axios.get(`${this.baseUrl}/aws/rds/instances`, { headers })
        .catch(error => {
          console.error('Error fetching RDS instances:', error);
          return { data: { error: error.response?.data?.error || error.message } };
        });
      
      // Fetch EC2 instances
      const ec2Response = await axios.get(`${this.baseUrl}/aws/ec2/instances`, { headers })
        .catch(error => {
          console.error('Error fetching EC2 instances:', error);
          return { data: { error: error.response?.data?.error || error.message } };
        });
      
      // Fetch SES statistics
      const sesResponse = await axios.get(`${this.baseUrl}/aws/ses/statistics`, { headers })
        .catch(error => {
          console.error('Error fetching SES statistics:', error);
          return { data: { error: error.response?.data?.error || error.message } };
        });
      
      // Combine all data
      return {
        s3Buckets: s3Response.data,
        cloudwatchMetrics: cloudwatchResponse.data,
        lambdaFunctions: lambdaResponse.data,
        rdsInstances: rdsResponse.data,
        ec2Instances: ec2Response.data,
        sesStats: sesResponse.data,
        services: {
          s3: !s3Response.data.error,
          cloudwatch: !cloudwatchResponse.data.error,
          lambda: !lambdaResponse.data.error,
          rds: !rdsResponse.data.error,
          ec2: !ec2Response.data.error,
          ses: !sesResponse.data.error
        }
      };
    } catch (error) {
      console.error('Error fetching AWS metrics:', error);
      return {
        error: error.message || 'Failed to fetch AWS metrics'
      };
    }
  }

  // Get Cloudflare metrics
  async getCloudflareMetrics() {
    // In development mode, return mock data
    if (process.env.NODE_ENV === 'development' && !this.cloudflareApiKey) {
      console.log('Using mock Cloudflare data for development');
      return this.getMockCloudflareMetrics();
    }
    
    try {
      // Check if API credentials are available
      if ((!this.cloudflareApiKey && !this.cloudflareApiToken) || !this.cloudflareZoneId) {
        return {
          error: 'Cloudflare API credentials not configured. Please add the required environment variables.'
        };
      }
      
      // Set up headers based on authentication method
      let headers = { 'Content-Type': 'application/json' };
      
      if (this.cloudflareApiToken) {
        // Use API Token (preferred)
        headers['Authorization'] = `Bearer ${this.cloudflareApiToken}`;
      } else {
        // Fall back to API Key
        headers['X-Auth-Email'] = this.cloudflareEmail;
        headers['X-Auth-Key'] = this.cloudflareApiKey;
      }
      
      // Try a longer date range - 90 days instead of 30
      const endDate = new Date();
      const startDate = new Date();
      startDate.setDate(startDate.getDate() - 90);
      
      // Format dates for Cloudflare API
      const since = startDate.toISOString().split('T')[0];
      const until = endDate.toISOString().split('T')[0];
      
      // Use headers in API calls
      const trafficResponse = await axios.get(
        `https://api.cloudflare.com/client/v4/zones/${this.cloudflareZoneId}/analytics/dashboard`,
        {
          params: { since, until, continuous: true },
          headers
        }
      );
      
      // Check if the zone has any traffic data
      if (trafficResponse.data.success && 
          (!trafficResponse.data.result.totals || 
           !trafficResponse.data.result.timeseries || 
           trafficResponse.data.result.timeseries.length === 0)) {
        console.log('No traffic data available for this zone');
        return {
          traffic: {
            totals: { requests: { all: 0 }, bandwidth: { all: 0 }, threats: { all: 0 }, pageviews: { all: 0 } },
            timeseries: []
          },
          security: [],
          message: 'This zone has no traffic data for the selected period. This could be because the site is new, has very low traffic, or analytics has been disabled.'
        };
      }
      
      // Check if the request was successful
      if (!trafficResponse.data.success) {
        console.error('Cloudflare API error:', trafficResponse.data.errors);
        return {
          error: `Cloudflare API error: ${trafficResponse.data.errors[0]?.message || 'Unknown error'}`
        };
      }
      
      // Fetch security events (firewall events)
      const securityResponse = await axios.get(
        `https://api.cloudflare.com/client/v4/zones/${this.cloudflareZoneId}/security/events`,
        {
          params: {
            since: startDate.toISOString(),
            until: endDate.toISOString(),
            per_page: 25
          },
          headers: {
            'X-Auth-Email': this.cloudflareEmail,
            'X-Auth-Key': this.cloudflareApiKey,
            'Content-Type': 'application/json'
          }
        }
      );
      
      // Combine the data
      return {
        traffic: trafficResponse.data.result,
        security: securityResponse.data.success ? securityResponse.data.result : []
      };
    } catch (error) {
      console.error('Error fetching Cloudflare metrics:', error);
      
      // Provide a more specific error message based on the error
      if (error.response) {
        // The request was made and the server responded with a status code
        // that falls out of the range of 2xx
        if (error.response.status === 403) {
          return {
            error: 'Authentication failed. Please check your Cloudflare API credentials.'
          };
        } else if (error.response.status === 429) {
          return {
            error: 'Rate limit exceeded. Please try again later.'
          };
        } else {
          return {
            error: `Cloudflare API error: ${error.response.data?.errors?.[0]?.message || error.message}`
          };
        }
      } else if (error.request) {
        // The request was made but no response was received
        return {
          error: 'No response from Cloudflare API. Please check your internet connection.'
        };
      } else {
        // Something happened in setting up the request that triggered an Error
        return {
          error: `Error setting up request: ${error.message}`
        };
      }
    }
  }

  // Mock AWS metrics for development
  getMockAWSMetrics() {
    return {
      s3: {
        buckets: 5,
        totalStorage: 1024, // GB
        storageUsed: 512, // GB
        requestsPerDay: 15000,
        errorRate: 0.5, // %
        costThisMonth: 45.75 // $
      },
      lambda: {
        functions: 12,
        invocationsPerDay: 8500,
        averageDuration: 320, // ms
        errorRate: 1.2, // %
        costThisMonth: 28.50 // $
      },
      dynamodb: {
        tables: 8,
        readCapacityUnits: 25,
        writeCapacityUnits: 15,
        storageUsed: 5, // GB
        averageResponseTime: 12, // ms
        costThisMonth: 18.25 // $
      },
      totalCost: {
        thisMonth: 92.50, // $
        lastMonth: 87.25, // $
        projected: 98.75 // $
      },
      alerts: [
        {
          service: 'Lambda',
          message: 'Function "processUploads" has 3.5% error rate',
          severity: 'warning',
          timestamp: new Date(Date.now() - 3600000).toISOString()
        },
        {
          service: 'S3',
          message: 'Bucket "user-uploads" approaching storage limit (85%)',
          severity: 'info',
          timestamp: new Date(Date.now() - 86400000).toISOString()
        }
      ]
    };
  }

  // Mock Cloudflare metrics for development
  getMockCloudflareMetrics() {
    return {
      traffic: {
        requests: 125000,
        bandwidth: 1250, // GB
        pageviews: 85000,
        uniqueVisitors: 32000
      },
      performance: {
        cacheHitRate: 85.2, // %
        averageResponseTime: 75, // ms
        timeToFirstByte: 45, // ms
        pageLoadTime: 1.2 // seconds
      },
      security: {
        threatsBlocked: 1250,
        botTraffic: 15.5, // %
        topAttackTypes: [
          { type: 'SQL Injection', count: 450 },
          { type: 'XSS', count: 320 },
          { type: 'DDoS', count: 180 },
          { type: 'File Inclusion', count: 150 },
          { type: 'Other', count: 150 }
        ]
      },
      countries: [
        { country: 'United States', requests: 65000 },
        { country: 'United Kingdom', requests: 15000 },
        { country: 'Germany', requests: 12000 },
        { country: 'Canada', requests: 10000 },
        { country: 'France', requests: 8000 },
        { country: 'Other', requests: 15000 }
      ],
      status: {
        uptime: 99.98, // %
        lastIncident: new Date(Date.now() - 15 * 86400000).toISOString()
      }
    };
  }

  // Add this debugging method to your ExternalAnalyticsService class
  async testCloudflareConnection() {
    try {
      console.log('Testing Cloudflare connection with:');
      console.log('Zone ID:', this.cloudflareZoneId ? 'Configured (hidden for security)' : 'Missing');
      console.log('Email:', this.cloudflareEmail ? 'Configured (hidden for security)' : 'Missing');
      console.log('API Key:', this.cloudflareApiKey ? 'Configured (hidden for security)' : 'Missing');
      
      // Test the connection with a simple API call
      const response = await axios.get(
        `https://api.cloudflare.com/client/v4/zones/${this.cloudflareZoneId}`,
        {
          headers: {
            'X-Auth-Email': this.cloudflareEmail,
            'X-Auth-Key': this.cloudflareApiKey,
            'Content-Type': 'application/json'
          }
        }
      );
      
      console.log('Cloudflare API response:', response.data.success ? 'Success' : 'Failed');
      return {
        success: response.data.success,
        zoneName: response.data.result?.name || 'Unknown',
        status: response.data.result?.status || 'Unknown'
      };
    } catch (error) {
      console.error('Cloudflare connection test failed:', error);
      return {
        success: false,
        error: error.response?.data?.errors?.[0]?.message || error.message,
        status: error.response?.status
      };
    }
  }

  // Update the getAWSWebsiteStatus method to use the proxy

  async getAWSWebsiteStatus() {
    if (process.env.NODE_ENV === 'development' && !this.awsConfigured) {
      // Return mock data for development
      return {
        websiteHosting: false,
        s3Endpoint: null,
        cloudfrontEnabled: false,
        cloudfrontDomain: null,
        message: 'Using mock data for development'
      };
    }
    
    if (!this.awsConfigured) {
      return {
        error: 'AWS credentials not configured. Please add the required environment variables.'
      };
    }
    
    try {
      // Use axios to call our proxy endpoints instead of direct AWS SDK calls
      const accessKeyId = process.env.REACT_APP_AWS_ACCESS_KEY_ID;
      const secretAccessKey = process.env.REACT_APP_AWS_SECRET_ACCESS_KEY;
      const region = process.env.REACT_APP_AWS_REGION || 'us-east-1';
      
      // Common headers for AWS requests
      const headers = {
        'X-AWS-Access-Key-ID': accessKeyId,
        'X-AWS-Secret-Access-Key': secretAccessKey,
        'X-AWS-Region': region
      };
      
      // List all S3 buckets through proxy
      const bucketsResponse = await axios.get('/api/proxy/aws/s3/buckets', { headers });
      
      if (!bucketsResponse.data.success) {
        throw new Error(bucketsResponse.data.error || 'Failed to list S3 buckets');
      }
      
      const buckets = bucketsResponse.data.data.Buckets || [];
      
      // Check each bucket for website configuration
      let websiteHosting = false;
      let s3Endpoint = null;
      
      for (const bucket of buckets) {
        try {
          const websiteResponse = await axios.get(`/api/proxy/aws/s3/website/${bucket.Name}`, { headers });
          
          if (websiteResponse.data.success && websiteResponse.data.data) {
            websiteHosting = true;
            s3Endpoint = `${bucket.Name}.s3-website-${region}.amazonaws.com`;
            break;
          }
        } catch (err) {
          // This bucket doesn't have website hosting enabled
          continue;
        }
      }
      
      // Check for CloudFront distributions through proxy
      const cloudfrontResponse = await axios.get('/api/proxy/aws/cloudfront/distributions', { headers });
      
      if (!cloudfrontResponse.data.success) {
        throw new Error(cloudfrontResponse.data.error || 'Failed to list CloudFront distributions');
      }
      
      const distributions = cloudfrontResponse.data.data.DistributionList?.Items || [];
      
      let cloudfrontEnabled = false;
      let cloudfrontDomain = null;
      
      if (distributions.length > 0) {
        cloudfrontEnabled = true;
        cloudfrontDomain = distributions[0].DomainName;
      }
      
      return {
        websiteHosting,
        s3Endpoint,
        cloudfrontEnabled,
        cloudfrontDomain
      };
    } catch (error) {
      console.error('Error fetching AWS website status:', error);
      return {
        error: `Failed to fetch AWS website status: ${error.message}`
      };
    }
  }
}

// Create and export singleton instance
const externalAnalyticsService = new ExternalAnalyticsService();
export default externalAnalyticsService;